import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactInputVerificationCode from 'react-input-verification-code';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';

import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { Routes } from '../../config/routes';
import { ValidationContext } from '../../providers/ValidationContext';
import { EmailValidationContext } from '../../providers/EmailValidationContext';
import { EmailErrorType } from '../../providers/email/emailCapture';
import SupportButton from '../../common/components/Button/SupportButton';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

enum ErrorMessageI18n {
  incorrectToken = 'screens.email.token.codeIncorrect',
  network = 'screens.email.token.networkError',
}

function EmailVerifyScreen(): JSX.Element {
  const TOKEN_LENGTH = 5;
  const COUNTDOWN_DURATION = 60;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [nextButtonEnabled, setNextButtonEnabled] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(ErrorMessageI18n.incorrectToken);
  const [resendCountdownSeconds, setResendCountdownSeconds] = useState(COUNTDOWN_DURATION);
  const [countdownTimerRunning, setCountdownTimerRunning] = useState(false);
  const timer: { current: NodeJS.Timeout | undefined } = useRef(undefined);
  const { getRouteForNextStep } = useContext(ValidationContext);
  const { setToken, verifyEmail, submitEmail, email, token } = useContext(EmailValidationContext);

  const resetCounter = () => {
    setCountdownTimerRunning(false);
    if (timer.current) {
      clearInterval(timer.current);
    }
    timer.current = undefined;
    setResendCountdownSeconds(COUNTDOWN_DURATION);
    setCountdownTimerRunning(true);
  };

  const stopCounter = () => {
    setCountdownTimerRunning(false);
    if (timer.current) {
      clearInterval(timer.current);
    }
    timer.current = undefined;
  };

  useEffect(() => {
    if (countdownTimerRunning) {
      if (!timer.current) {
        timer.current = setInterval(() => setResendCountdownSeconds((prevCounter) => prevCounter - 1), 1000);
      } else if (resendCountdownSeconds <= 0 && timer.current) {
        // stop if seconds remaining reaches 0
        clearInterval(timer.current);
        timer.current = undefined;
      }
    } else {
      if (timer.current) {
        clearInterval(timer.current);
      }
      timer.current = undefined;
    }
  }, [resendCountdownSeconds, countdownTimerRunning]);

  useEffect(() => {
    setToken('');
    resetCounter();

    return function cleanup() {
      stopCounter();
    };
  }, []);

  useEffect(() => {
    if (validator.isNumeric(token, { no_symbols: true }) && token.length === TOKEN_LENGTH) {
      handleConfirm();
    }
  }, [token]);

  const tokenChanged = (newToken: string) => {
    setToken(newToken);
    if (newToken.length > 0) {
      setShowErrorMessage(false);
    }
    setNextButtonEnabled(newToken.length >= TOKEN_LENGTH);
  };

  // If the user has entered all ${TOKEN_LENGTH} digits of the token and it's numeric
  // then set the next button enabled
  const tokenCompleted = (newToken: string) => {
    if (validator.isNumeric(newToken, { no_symbols: true }) && newToken.length === TOKEN_LENGTH) {
      setNextButtonEnabled(true);
    }
  };

  const handleConfirm = async () => {
    setShowErrorMessage(false);
    setLoading(true);
    const result = await verifyEmail();
    setLoading(false);
    if (result.isSuccessful()) {
      stopCounter();
      const route = await getRouteForNextStep();
      if (route) navigate(route);
      return;
    }
    document?.getElementById('one-time-code')?.focus();
    const { errorType } = result.failure();
    switch (errorType) {
      case EmailErrorType.token:
        setNextButtonEnabled(false);
        setErrorMessage(ErrorMessageI18n.incorrectToken);
        setShowErrorMessage(true);
        setToken('');
        break;
      case EmailErrorType.network:
        setNextButtonEnabled(true);
        setErrorMessage(ErrorMessageI18n.network);
        setShowErrorMessage(true);
        break;
      default:
      case EmailErrorType.generic:
      case EmailErrorType.retriesExhausted:
        stopCounter();
        navigate(Routes.VALIDATION_REJECTED);
    }
  };

  const handleResend = async () => {
    setShowErrorMessage(false);
    setLoading(true);
    setToken('');
    setNextButtonEnabled(false);
    const result = await submitEmail();
    setLoading(false);
    document?.getElementById('one-time-code')?.focus();
    if (result.isSuccessful()) {
      resetCounter();
    }
  };

  const handleChangeEmail = () => {
    navigate(Routes.EMAIL_CAPTURE);
  };

  return (
    <Container>
      <TitleContainer>
        <h1 data-testid="EMAIL_TOKEN_HEADER">{t('screens.email.token.title')}</h1>
      </TitleContainer>

      <div className="text-lg leading-6 text-textParagraph" data-testid="EMAIL_TOKEN_DESCRIPTION">
        <p>{t('screens.email.token.description1')}</p>
      </div>

      <div className="text-lg leading-6 text-textParagraph" data-testid="EMAIL_TOKEN_EMAIL">
        <p>{email}</p>
      </div>

      <div className="mb-4 ml-0 wide:ml-0 medium:ml-0 flex items-center">
        <div className="text-m leading-5">
          <span
            onClick={handleChangeEmail}
            onKeyDown={handleChangeEmail}
            role="button"
            tabIndex={0}
            className="textLink"
            data-testid="EMAIL_TOKEN_CHANGE_EMAIL"
          >
            {t('screens.email.token.changeAddressLabel')}{' '}
          </span>
        </div>
      </div>

      <div className="text-lg leading-6 mt-2 mb-2 text-textParagraph" data-testid="EMAIL_TOKEN_ENTER_TOKEN_LABEL">
        <p>{t('screens.email.token.enterCodeLabel')}</p>
      </div>

      <div className="verification-code-input" data-testid="EMAIL_TOKEN_ENTRY_INPUT">
        <ReactInputVerificationCode
          length={TOKEN_LENGTH}
          autoFocus
          placeholder=""
          onChange={tokenChanged}
          value={token}
          onCompleted={tokenCompleted}
        />
      </div>

      <>
        {showErrorMessage && (
          <div className="text-lg font-medium leading-6 text-warning mt-4" data-testid="EMAIL_TOKEN_INCORRECT">
            <p>{t(errorMessage)}</p>
          </div>
        )}
      </>
      <div className="mt-4 mb-4 ml-0 wide:ml-0 medium:ml-0 flex items-center">
        {resendCountdownSeconds > 0 && (
          <span className="text-m leading-5 text-textTertiary font-semibold">
            <p>{t('screens.email.token.resendTimerLabel', { secondsRemaining: resendCountdownSeconds })}</p>
          </span>
        )}
        {resendCountdownSeconds <= 0 && (
          <span
            onClick={handleResend}
            onKeyDown={handleResend}
            role="button"
            tabIndex={0}
            className="text-m leading-5 text-textTertiary hover:text-primaryHover font-semibold"
          >
            <p>{t('screens.email.token.resendLabel')}</p>
          </span>
        )}
      </div>

      <ButtonContainer bottomMargin="mb-0">
        <Button
          style={ButtonStyle.DEFAULT}
          onClick={handleConfirm}
          disabled={!nextButtonEnabled}
          loading={loading}
          dataTestid="CONFIRM_BUTTON"
        >
          <span>{t('screens.email.token.buttonLabel')}</span>
        </Button>
      </ButtonContainer>
      <SupportButton />
    </Container>
  );
}
export default withRouteAnalytics(EmailVerifyScreen, Screens.EMAIL_TOKEN_CAPTURE);
